// R116 #1 "Welcome home" — the living stage behind every sign-in screen:
//   • OS-drawn cursors: a blue house-pin arrow on the stage/card and a key over
//     Sign In, built here from the :root tokens and set as --au-cur-home /
//     --au-cur-key on <html> (Skeptic D1b/D8). The native cursor is NEVER
//     hidden; inputs keep the text cursor, links the pointer (auth.css).
//   • a follower ring + pointer spotlight + a little skyline parallax, only for
//     a fine pointer with no reduced-motion preference. ONE rAF loop that runs
//     only while something is still moving and stops by itself; cancelled
//     while the tab is hidden. Pointer input is read from one passive listener.
//   • skyline windows switching on/off on ONE timer that stops by itself after
//     ≈10 s (WCAG 2.2.2) and pauses while the tab is hidden; a click on the
//     empty stage pops a tiny roof tick and lights one more window.
//   • dusk after 18:00 local (and before 06:00): more windows lit, deeper wash.
// mountAuthFx() is idempotent (the stage survives login ⇄ forgot swaps) and
// returns destroy(), which removes every node, listener, timer and class it
// added — the shared-crew overlay hands over to the app in the same page.
import { skylineSvg, DAY_WINDOWS, DUSK_WINDOWS } from './authArt';

type Stats = {
  mounted: boolean; dusk: boolean; ringOn: boolean; paused: boolean;
  rafFrames: number; rafRunning: boolean;
  timerTicks: number; timerRunning: boolean; timerDone: boolean; timerElapsed: number;
  clicks: number;
};
const stats: Stats = { mounted: false, dusk: false, ringOn: false, paused: false, rafFrames: 0, rafRunning: false, timerTicks: 0, timerRunning: false, timerDone: false, timerElapsed: 0, clicks: 0 };
/** Instrumentation for scripts/test_login_dom.mjs (Skeptic D5: count our own loop, not the display's frames). */
export function authFxStats(): Stats { return { ...stats }; }

// The window timer: one toggle every ~0.6–1.3 s, a fixed sequence, ≈9.6 s total.
const SEQ: number[] = [7, 20, 33, 12, 25, 3, 36, 16, 29, 9, 22, 34];
const GAP = (k: number) => 600 + ((k * 397) % 700);
export const TIMER_BUDGET_MS = 10000;

let destroyFn: (() => void) | null = null;

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** The two OS cursors (32×32 with natural size + a keyword fallback — Skeptic D8). */
export function cursorValues(): { home: string; key: string } {
  const fill = css('--accent') || 'blue';
  const edge = css('--card') || 'white';
  const win = css('--warning-border') || 'gold';
  const svg = (body: string) => `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">${body}</svg>`)}")`;
  // the house-pin arrow: a steep gable whose apex is the arrow tip (hotspot 3 3), tilted
  // like the OS arrow (left edge ≈ vertical, right edge ≈ 45°), the walls as its tail, a lit window
  const home = svg(`<g transform="translate(3 3) rotate(-22) scale(1.08)"><path d="M0 0 8 15.6H5.3V22H-5.3V15.6H-8Z" fill="${fill}" stroke="${edge}" stroke-width="1.5" stroke-linejoin="round"/><rect x="-1.9" y="9.2" width="3.8" height="3.8" rx=".7" fill="${win}"/></g>`);
  // a key, blade tip at the hotspot (4 4), bow down-right
  const key = svg(`<g transform="translate(4 4) rotate(-45)"><path d="M-1.9 0H1.9V3.2H4.6V5.6H1.9V7.8H4V10.2H1.9V14.4A6.2 6.2 0 1 1 -1.9 14.4Z" fill="${fill}" stroke="${edge}" stroke-width="1.5" stroke-linejoin="round"/><circle cx="0" cy="20.3" r="2.2" fill="${edge}"/></g>`);
  return { home: `${home} 3 3, auto`, key: `${key} 4 4, pointer` };
}

function isDusk(now: Date): boolean { const h = now.getHours(); return h >= 18 || h < 6; }

export type FxOptions = { now?: Date };

export function mountAuthFx(opts: FxOptions = {}): () => void {
  if (destroyFn) return destroyFn;
  const root = document.documentElement;
  const doc = document;
  const off: Array<() => void> = [];
  const on = <K extends string>(t: EventTarget, ev: K, fn: (e: any) => void, o?: AddEventListenerOptions) => { t.addEventListener(ev, fn, o); off.push(() => t.removeEventListener(ev, fn, o)); };

  // ── html classes (left alone on destroy when the boot splash set them first) ──
  const hadOn = root.classList.contains('au-on');
  const hadDusk = root.classList.contains('au-dusk');
  const dusk = isDusk(opts.now || new Date());
  root.classList.add('au-on');
  root.classList.toggle('au-dusk', dusk || hadDusk);
  stats.dusk = dusk || hadDusk;

  // ── cursors on :root (the stage is a sibling of #app, so they must live here) ──
  const cur = cursorValues();
  root.style.setProperty('--au-cur-home', cur.home);
  root.style.setProperty('--au-cur-key', cur.key);

  // ── the stage ──
  const stage = doc.createElement('div');
  stage.className = 'au-stage';
  stage.setAttribute('aria-hidden', 'true');
  stage.innerHTML = `<div class="au-dots"></div><div class="au-spot"></div><div class="au-sky">${skylineSvg()}</div>`;
  doc.body.appendChild(stage);
  const spot = stage.querySelector('.au-spot') as HTMLElement;
  const skySvg = stage.querySelector('.au-sky-svg') as SVGElement;
  const windows = Array.from(stage.querySelectorAll<SVGElement>('.au-w'));
  const lit = new Set<number>([...DAY_WINDOWS, ...(stats.dusk ? DUSK_WINDOWS : [])]);
  windows.forEach((w, i) => w.classList.toggle('on', lit.has(i)));

  const ring = doc.createElement('div');
  ring.className = 'au-ring';
  ring.setAttribute('aria-hidden', 'true');
  doc.body.appendChild(ring);
  const tick = doc.createElement('div');
  tick.className = 'au-tick';
  tick.setAttribute('aria-hidden', 'true');
  tick.innerHTML = `<svg viewBox="0 0 22 22" width="22" height="22"><path d="M4 12 11 5.5 18 12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.2 15.4 10.3 17.4 14.2 13.4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  doc.body.appendChild(tick);

  // ── media gates (live: an OS toggle / a mouse plugged in) ──
  const mqFine = window.matchMedia('(pointer: fine)');
  const mqCalm = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fancy = () => mqFine.matches && !mqCalm.matches;
  const hidden = () => doc.visibilityState === 'hidden';

  // ── the window timer ──
  let seqAt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const toggleWindow = (i: number) => { const w = windows[i]; if (w) w.classList.toggle('on'); };
  const schedule = () => {
    if (timer || stats.timerDone || mqCalm.matches || hidden()) { stats.timerRunning = !!timer; return; }
    if (seqAt >= SEQ.length || stats.timerElapsed + GAP(seqAt) > TIMER_BUDGET_MS) { stats.timerDone = true; stats.timerRunning = false; return; }
    const gap = GAP(seqAt);
    stats.timerRunning = true;
    timer = setTimeout(() => {
      timer = null;
      stats.timerElapsed += gap;
      toggleWindow(SEQ[seqAt++]);
      stats.timerTicks++;
      schedule();
    }, gap);
  };
  const stopTimer = () => { if (timer) { clearTimeout(timer); timer = null; } stats.timerRunning = false; };
  off.push(stopTimer);

  // ── the ring / spotlight loop ──
  let tx = -100, ty = -100, rx = -100, ry = -100, sx = -100, sy = -100, px = 0, ppx = 0;
  let seen = false, raf = 0;
  let snapEl: HTMLElement | null = null, snapRect: DOMRect | null = null;
  let mx = 0, my = 0;
  const frame = () => {
    raf = 0;
    stats.rafFrames++;
    let gx = tx, gy = ty;
    if (snapEl && snapRect) { gx = snapRect.left + snapRect.width / 2 + mx; gy = snapRect.top + snapRect.height / 2 + my; }
    rx += (gx - rx) * 0.2; ry += (gy - ry) * 0.2;
    sx += (tx - sx) * 0.12; sy += (ty - sy) * 0.12;
    ppx = ((tx / Math.max(1, window.innerWidth)) - 0.5) * -12;
    px += (ppx - px) * 0.08;
    ring.style.transform = `translate3d(${rx.toFixed(1)}px, ${ry.toFixed(1)}px, 0) translate(-50%, -50%)`;
    spot.style.transform = `translate3d(${(sx - 280).toFixed(1)}px, ${(sy - 280).toFixed(1)}px, 0)`;
    if (skySvg) skySvg.style.transform = `translate3d(${px.toFixed(2)}px, 0, 0)`;
    const moving = Math.abs(gx - rx) > 0.2 || Math.abs(gy - ry) > 0.2 || Math.abs(tx - sx) > 0.2 || Math.abs(ty - sy) > 0.2 || Math.abs(ppx - px) > 0.05;
    if (moving && !hidden()) raf = requestAnimationFrame(frame);
    stats.rafRunning = !!raf;
  };
  const kick = () => { if (!raf && seen && !hidden() && stats.ringOn) { raf = requestAnimationFrame(frame); stats.rafRunning = true; } };
  const stopLoop = () => { if (raf) cancelAnimationFrame(raf); raf = 0; stats.rafRunning = false; };
  off.push(stopLoop);

  const setRingMode = (t: Element | null) => {
    const el = t instanceof Element ? t : null;
    const magnet = el?.closest<HTMLElement>('[data-au-magnet]') || null;
    const textish = el?.closest('input, textarea, select, label, [contenteditable=""], [contenteditable="true"]');
    const clicky = el?.closest('a, button, [role="button"]');
    if (magnet !== snapEl) {
      if (snapEl) { snapEl.style.setProperty('--mx', '0px'); snapEl.style.setProperty('--my', '0px'); }
      snapEl = magnet && !(magnet as HTMLButtonElement).disabled ? magnet : null;
      snapRect = snapEl ? snapEl.getBoundingClientRect() : null;   // cached on enter — no per-frame layout reads
      mx = 0; my = 0;
      if (snapRect) { ring.style.setProperty('--snap-w', `${Math.round(snapRect.width + 10)}px`); ring.style.setProperty('--snap-h', `${Math.round(snapRect.height + 10)}px`); }
    }
    ring.classList.toggle('is-snap', !!snapEl);
    ring.classList.toggle('is-hide', !!textish && !snapEl);
    ring.classList.toggle('is-grow', !!clicky && !snapEl && !textish);
  };

  const onMove = (e: PointerEvent) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    if (!stats.ringOn) return;
    tx = e.clientX; ty = e.clientY;
    if (!seen) { seen = true; rx = sx = tx; ry = sy = ty; ring.classList.add('on'); spot.classList.add('on'); }
    if (snapEl && snapRect) {
      // the magnet: Sign In eases up to 3 px toward the pointer (CSS vars, composed into its one transform — Skeptic D2)
      mx = Math.max(-1, Math.min(1, (tx - (snapRect.left + snapRect.width / 2)) / (snapRect.width / 2))) * 3;
      my = Math.max(-1, Math.min(1, (ty - (snapRect.top + snapRect.height / 2)) / (snapRect.height / 2))) * 2;
      snapEl.style.setProperty('--mx', `${mx.toFixed(2)}px`);
      snapEl.style.setProperty('--my', `${my.toFixed(2)}px`);
    }
    kick();
  };
  const onOver = (e: PointerEvent) => { if (stats.ringOn && (!e.pointerType || e.pointerType === 'mouse')) setRingMode(e.target as Element); };
  const onLeaveDoc = (e: PointerEvent) => { if (!e.relatedTarget && stats.ringOn) { ring.classList.remove('on'); spot.classList.remove('on'); seen = false; } };
  const onDown = () => { if (stats.ringOn) ring.classList.add('is-down'); };
  const onUp = () => ring.classList.remove('is-down');
  const onScroll = () => { if (snapEl) snapRect = snapEl.getBoundingClientRect(); };

  const applyGates = () => {
    const want = fancy();
    if (want !== stats.ringOn) {
      stats.ringOn = want;
      root.classList.toggle('au-fx-fine', want);
      if (!want) { stopLoop(); ring.classList.remove('on', 'is-snap', 'is-grow', 'is-hide', 'is-down'); spot.classList.remove('on'); seen = false; if (snapEl) { snapEl.style.setProperty('--mx', '0px'); snapEl.style.setProperty('--my', '0px'); snapEl = null; } }
    }
    if (mqCalm.matches) stopTimer(); else schedule();
  };
  on(mqFine, 'change', applyGates);
  on(mqCalm, 'change', applyGates);

  on(doc, 'pointermove', onMove, { passive: true });
  on(doc, 'pointerover', onOver, { passive: true });
  on(doc, 'pointerout', onLeaveDoc, { passive: true });
  on(doc, 'pointerdown', onDown, { passive: true });
  on(doc, 'pointerup', onUp, { passive: true });
  on(window, 'scroll', onScroll, { passive: true });
  on(window, 'resize', onScroll, { passive: true });

  // a click on the empty stage (never inside a card) pops a roof tick and lights a window (Skeptic D1d)
  let nextLight = 0;
  const onClick = (e: MouseEvent) => {
    const t = e.target as Element | null;
    if (e.button !== 0 || !t || !(t instanceof Element) || t.closest('.au-card, a, button, input, label, select, textarea')) return;
    stats.clicks++;
    const dark = windows.map((w, i) => (w.classList.contains('on') ? -1 : i)).filter(i => i >= 0);
    if (dark.length) toggleWindow(dark[(nextLight++ * 7) % dark.length]);
    if (mqCalm.matches) return;
    tick.style.setProperty('--tx', `${e.clientX}px`);
    tick.style.setProperty('--ty', `${e.clientY}px`);
    tick.classList.remove('go');
    void tick.offsetWidth;   // restart the one recycled element's animation
    tick.classList.add('go');
  };
  on(doc, 'click', onClick);

  // hidden tab → nothing runs; stage CSS animations pause too (.au-paused)
  const onVis = () => {
    const h = hidden();
    stats.paused = h;
    root.classList.toggle('au-paused', h);
    if (h) { stopTimer(); stopLoop(); } else { schedule(); kick(); }
  };
  on(doc, 'visibilitychange', onVis);

  stats.mounted = true;
  stats.rafFrames = 0; stats.rafRunning = false; stats.timerTicks = 0; stats.timerDone = false; stats.timerElapsed = 0; stats.clicks = 0; stats.ringOn = false;
  applyGates();
  onVis();

  destroyFn = () => {
    off.splice(0).forEach(f => { try { f(); } catch { /* gone */ } });
    stage.remove(); ring.remove(); tick.remove();
    if (snapEl) { snapEl.style.removeProperty('--mx'); snapEl.style.removeProperty('--my'); }
    root.style.removeProperty('--au-cur-home');
    root.style.removeProperty('--au-cur-key');
    root.classList.remove('au-fx-fine', 'au-paused', 'au-celebrate');
    if (!hadOn) root.classList.remove('au-on');
    if (!hadDusk) root.classList.remove('au-dusk');
    Object.assign(stats, { mounted: false, ringOn: false, paused: false, rafRunning: false, timerRunning: false });
    destroyFn = null;
  };
  return destroyFn;
}

/** Success: every window in the street comes on (a quick cascade; instant under reduced motion). */
export function celebrateAuthFx(): void {
  document.documentElement.classList.add('au-celebrate');
}

/** Tear the stage down if it is mounted (the shared-crew overlay hands over to the app). */
export function destroyAuthFx(): void { if (destroyFn) destroyFn(); }
